import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import AddGuestModal from './AddGuestModal';
import SuggestPlayerModal from './SuggestPlayerModal';
import CancelTeeTimeModal from './CancelTeeTimeModal';
import EditTeeTimeModal from './EditTeeTimeModal';
import { EVERYONE_GROUP, APP_WIDE_GROUP } from '../constants';

const fmtDate = (d) => {
  if (!d) return 'No date';
  const dateStr = d.includes('T') ? d.split('T')[0] : d;
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};
const fmtTime = (t) => {
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${m.toString().padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};
const fmtHandicap = (h) => {
  if (h === null || h === undefined) return null;
  const n = parseFloat(h);
  return n > 0 ? `+${n}` : String(n);
};

// Build a map of player id → chip display name. Default is the first name; but
// when two or more players in the SAME tee time share a first name, those
// players get a last initial appended ("Mike R.") so they're distinguishable
// at a glance. Players with unique first names are unaffected. The full name
// is always reachable via the chip's tooltip.
const computeDisplayNames = (players = []) => {
  const firstNameCounts = {};
  for (const p of players) {
    const first = (p.name || '').trim().split(/\s+/)[0] || '';
    const key = first.toLowerCase();
    firstNameCounts[key] = (firstNameCounts[key] || 0) + 1;
  }
  const map = {};
  for (const p of players) {
    const parts = (p.name || '').trim().split(/\s+/);
    const first = parts[0] || '';
    const last  = parts.length > 1 ? parts[parts.length - 1] : '';
    if (firstNameCounts[first.toLowerCase()] > 1 && last) {
      map[p.id] = `${first} ${last[0].toUpperCase()}.`;
    } else {
      map[p.id] = first;
    }
  }
  return map;
};

// ─── Reusable hover / long-press tooltip ──────────────────────
// Wraps any element and shows a dark popover with `text` on hover (desktop)
// or long-press (touch, ~450ms). Auto-dismisses a few seconds after a
// long-press so it doesn't linger on mobile. The tooltip itself is
// pointer-events:none so it never blocks taps on the wrapped control.
//
//   <Tooltip text="Explanation here"><button>…</button></Tooltip>
//
// `place` controls which side it appears on ('top' default, or 'bottom').
// `wrapStyle` is merged onto the wrapper span (e.g. to keep layout tidy).
const Tooltip = ({ text, children, place = 'top', wrapStyle }) => {
  const [open, setOpen] = useState(false);
  const pressTimer = useRef(null);
  // When a long-press actually completes (tooltip shown), we suppress the
  // click that fires on release — otherwise long-pressing a button to read
  // its tooltip would also trigger the button (bad for destructive actions).
  const longPressFired = useRef(false);

  const startPress = () => {
    longPressFired.current = false;
    pressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      setOpen(true);
    }, 450);
  };
  const cancelPress = () => {
    if (pressTimer.current) { clearTimeout(pressTimer.current); pressTimer.current = null; }
  };
  const handleClickCapture = (e) => {
    // Capture phase: if this click is the tail of a long-press, eat it before
    // it reaches the wrapped control.
    if (longPressFired.current) {
      e.stopPropagation();
      e.preventDefault();
      longPressFired.current = false;
    }
  };
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => setOpen(false), 3200);
    return () => clearTimeout(t);
  }, [open]);

  if (!text) return children;

  const vertical = place === 'bottom'
    ? { top: '100%', marginTop: 6 }
    : { bottom: '100%', marginBottom: 6 };

  return (
    <span
      style={{ position: 'relative', display: 'inline-flex', ...wrapStyle }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onTouchStart={startPress}
      onTouchEnd={cancelPress}
      onTouchMove={cancelPress}
      onClickCapture={handleClickCapture}
    >
      {open && (
        <span
          style={{
            position: 'absolute', left: '50%', transform: 'translateX(-50%)',
            ...vertical,
            background: '#0d2b1a', color: '#e8f5ec',
            fontSize: 11, lineHeight: 1.4, fontWeight: 400,
            padding: '6px 9px', borderRadius: 7,
            width: 'max-content', maxWidth: 220, textAlign: 'center',
            boxShadow: '0 3px 12px rgba(0,0,0,0.28)', zIndex: 60,
            pointerEvents: 'none',
          }}
        >
          {text}
        </span>
      )}
      {children}
    </span>
  );
};

// Relative-time formatter for waitlist "joined X ago" display.
// "just now" / "5m ago" / "3h ago" / "2d ago" / falls back to short date.
const fmtJoinedAt = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0 || ms < 60 * 1000) return 'just now';
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// ─── Weather strip ────────────────────────────────────────────
const WeatherStrip = ({ weather }) => {
  if (!weather) return null;
  const rainWarning = weather.precipChance >= 50;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      background: rainWarning ? '#faf0d8' : '#f0faf4',
      borderRadius: 7, padding: '5px 9px', marginBottom: '0.65rem',
      fontSize: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <span style={{ fontSize: 15 }}>{weather.icon}</span>
        <span style={{ color: '#3a3a30', fontWeight: 500 }}>{weather.label}</span>
      </div>
      <div style={{ display: 'flex', gap: 8, color: '#5a5a4a', fontSize: 11 }}>
        <span>{weather.tempLow}–{weather.tempHigh}°F</span>
        {weather.precipChance > 0 && (
          <span style={{ color: rainWarning ? '#7a5a0a' : '#5a5a4a' }}>
            💧 {weather.precipChance}%
          </span>
        )}
        <span>💨 {weather.windSpeed} mph</span>
      </div>
    </div>
  );
};

// ─── Player chip with handicap + guest RSVP indicator ─────────
// Guests carry an RSVP status: 'invited' | 'confirmed' | 'declined'.
//   - confirmed → solid styling, counts toward the tee time
//   - invited   → dashed + amber tint, "invited" tag, doesn't count
//   - declined  → muted + strikethrough, "declined" tag, doesn't count
// When the viewer is the host, an invited or declined guest chip exposes
// quick status actions (✓ confirm / ✗ decline / ↩ reset).
//
// displayName: what to show on the chip. Normally the first name, but when
// two players in the same tee time share a first name the parent passes a
// disambiguated form ("Mike R."). The full name is always available via the
// hover / long-press tooltip.
const PlayerChip = ({ player, isHost, isCurrentUser, canRemove, onRemove, onProfileClick, viewerIsHost, onSetGuestStatus, displayName }) => {
  const hcp     = fmtHandicap(player.handicap);
  const isGuest = player.type === 'guest';
  const status  = isGuest ? (player.status || 'confirmed') : null;
  const isInvited  = status === 'invited';
  const isDeclined = status === 'declined';
  const clickable = !!onProfileClick && !isGuest && !isCurrentUser && !isHost;
  const shownName = displayName || player.name.split(' ')[0];

  // Full-name tooltip text — includes handicap (members) or guest status.
  const tipText = player.name
    + (hcp !== null && !isGuest ? ` · ${hcp} hcp` : '')
    + (isGuest ? ` · guest${status && status !== 'confirmed' ? ` (${status})` : ''}` : '');

  // Guest chip background/border by status
  const guestBg     = isDeclined ? '#f0eee8' : isInvited ? '#faf6e8' : '#f4f1eb';
  const guestColor  = isDeclined ? '#a8a498' : isInvited ? '#7a5a0a' : '#5a5a4a';
  const guestBorder = isDeclined ? '1px dashed #d8d4c8'
                    : isInvited  ? '1px dashed #d8c98a'
                    :              '1px dashed #c8c4b4';

  return (
    <Tooltip text={tipText}>
    <div
      onClick={clickable ? (e) => { e.stopPropagation(); onProfileClick(player); } : undefined}
      style={{
        position: 'relative',
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: isHost     ? '#0d2b1a'
                  : isCurrentUser ? '#2e7d4f'
                  : isGuest    ? guestBg
                  :               '#e8f5ec',
        borderRadius: 20, padding: '2px 8px', fontSize: 11,
        color: isHost      ? '#7ec99a'
             : isCurrentUser ? '#fff'
             : isGuest     ? guestColor
             :                '#1a5c32',
        border: isGuest ? guestBorder : 'none',
        cursor: clickable ? 'pointer' : 'default',
      }}
    >
      {isGuest && (
        <span style={{ fontSize: 9, opacity: 0.7 }}>👤</span>
      )}
      <span style={{ textDecoration: isDeclined ? 'line-through' : 'none' }}>
        {player.initials}
      </span>
      <span style={{ textDecoration: isDeclined ? 'line-through' : 'none' }}>
        {shownName}
        {isHost ? ' ★' : ''}
        {isCurrentUser && !isHost ? ' (you)' : ''}
        {isGuest ? ' · guest' : ''}
      </span>
      {/* Guest RSVP status tag */}
      {isGuest && (isInvited || isDeclined) && (
        <span style={{
          background: isInvited ? '#ebe0bd' : '#e0ddd4',
          color: isInvited ? '#7a5a0a' : '#8a8678',
          borderRadius: 10, padding: '0px 5px', fontSize: 9, fontWeight: 500,
        }}>
          {isInvited ? 'invited' : 'declined'}
        </span>
      )}
      {hcp !== null && !isGuest && (
        <span style={{
          background: 'rgba(255,255,255,0.25)', borderRadius: 10,
          padding: '0px 5px', fontSize: 10, fontWeight: 500,
        }}>
          {hcp}
        </span>
      )}
      {player.ghin_number && !isGuest && (
        <a
          href={`https://www.ghin.com/golfers/${player.ghin_number}`}
          target="_blank" rel="noreferrer"
          onClick={e => e.stopPropagation()}
          style={{
            background: 'rgba(255,255,255,0.2)', borderRadius: 10,
            padding: '0px 5px', fontSize: 9, fontWeight: 500,
            color: 'inherit', textDecoration: 'none', opacity: 0.85,
          }}
          title="View GHIN profile"
        >
          GHIN
        </a>
      )}
      {/* Host-only guest RSVP controls. Compact icon buttons; which ones show
          depends on current status so the host always has the sensible moves. */}
      {isGuest && viewerIsHost && onSetGuestStatus && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 1, marginLeft: 1 }}>
          {status !== 'confirmed' && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onSetGuestStatus(player, 'confirmed'); }}
              style={chipBtnStyle('#2e7d4f')}
              title="Mark confirmed"
              aria-label={`Mark ${player.name} confirmed`}
            >✓</button>
          )}
          {status !== 'declined' && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onSetGuestStatus(player, 'declined'); }}
              style={chipBtnStyle('#b5683c')}
              title="Mark declined"
              aria-label={`Mark ${player.name} declined`}
            >✗</button>
          )}
          {status !== 'invited' && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onSetGuestStatus(player, 'invited'); }}
              style={chipBtnStyle('#9a9a8a')}
              title="Reset to invited"
              aria-label={`Reset ${player.name} to invited`}
            >↩</button>
          )}
        </span>
      )}
      {canRemove && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(player.id); }}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#9a9a8a', fontSize: 18, padding: '4px 6px',
            margin: '-4px -4px -4px 0', lineHeight: 1,
            touchAction: 'manipulation', WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
          }}
          title={isGuest ? 'Remove guest' : 'Remove player'}
          aria-label={isGuest ? 'Remove guest' : 'Remove player'}
        >
          ×
        </button>
      )}
    </div>
    </Tooltip>
  );
};

// Small circular icon-button used for the host's guest RSVP quick-actions.
const chipBtnStyle = (color) => ({
  background: 'rgba(255,255,255,0.6)', border: `1px solid ${color}`,
  color, borderRadius: '50%', width: 16, height: 16, fontSize: 9,
  lineHeight: 1, padding: 0, cursor: 'pointer', display: 'inline-flex',
  alignItems: 'center', justifyContent: 'center',
  touchAction: 'manipulation', WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
});

// ─── Waitlist badge ───────────────────────────────────────────
const WaitlistBadge = ({ count }) => {
  if (!count || count < 1) return null;
  return (
    <span style={{
      fontSize: 10, fontWeight: 500, padding: '2px 7px', borderRadius: 20,
      background: '#faf0d8', color: '#7a5a0a',
    }}>
      {count} waiting
    </span>
  );
};

// ─── Group / visibility picker ────────────────────────────────
// v100: this used to be an absolutely-positioned dropdown rendered inside
// .tee-card, which sets `overflow: hidden` — so the list was clipped at the
// card's edge. Anything past the fold (including the Save / Cancel row) was
// invisible and unreachable, and what did render sat on top of the card's own
// content with no way to dismiss it.
//
// It's now a portal-rendered bottom sheet on document.body: it escapes the
// card's overflow and stacking context entirely, scrolls its own list, and
// closes on backdrop tap, ✕, or Esc.
const GroupPickerSheet = ({
  courseName, groups, groupsLoading, saving,
  isEveryone, isAppWide, selectedGroupIds,
  onToggleEveryone, onToggleAppWide, onToggleGroup,
  onSave, onClose,
}) => {
  // Keep the latest onClose without re-binding listeners on every render.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closeRef.current(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';   // no scrolling the page behind the sheet
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  const selectedCount = (isEveryone ? 1 : 0) + (isAppWide ? 1 : 0) + selectedGroupIds.length;

  const Row = ({ checked, onChange, color, emoji, name, hint }) => (
    <label
      style={{
        display: 'flex', alignItems: 'center', gap: 11, cursor: 'pointer',
        background: checked ? '#e8f5ec' : '#f9f7f3',
        border: `1.5px solid ${checked ? '#7ec99a' : '#e0dcd0'}`,
        borderRadius: 8, padding: '9px 11px', marginBottom: 6,
        transition: 'background 0.15s, border-color 0.15s',
      }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        style={{ width: 18, height: 18, flexShrink: 0, margin: 0,
                 accentColor: color || '#2e7d4f', cursor: 'pointer' }}
      />
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14, color: '#0d2b1a',
                       overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {emoji} {name}
        </span>
        {hint && (
          <span style={{ display: 'block', fontSize: 11, color: '#7a7a6a', marginTop: 1 }}>
            {hint}
          </span>
        )}
      </span>
    </label>
  );

  return createPortal(
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        background: 'rgba(13,43,26,0.45)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Choose who can see this tee time"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 460, background: '#fff',
          borderRadius: '16px 16px 0 0', boxShadow: '0 -8px 30px rgba(0,0,0,0.22)',
          display: 'flex', flexDirection: 'column', maxHeight: '82vh', minHeight: 0,
        }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8,
                      padding: '14px 16px 10px', borderBottom: '1px solid #f0ede4' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, color: '#0d2b1a' }}>
              Who can see this?
            </div>
            {courseName && (
              <div style={{ fontSize: 11, color: '#7a7a6a', marginTop: 1,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {courseName}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'none', border: 'none', cursor: 'pointer',
                     fontSize: 18, lineHeight: 1, color: '#7a7a6a',
                     padding: 4, margin: -4, flexShrink: 0 }}>
            ✕
          </button>
        </div>

        {/* Scrollable list — the whole point of the rewrite */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto',
                      WebkitOverflowScrolling: 'touch', padding: '12px 16px' }}>
          <Row
            checked={isEveryone}
            onChange={onToggleEveryone}
            color={EVERYONE_GROUP.color}
            emoji={EVERYONE_GROUP.emoji}
            name={EVERYONE_GROUP.name}
            hint="Everyone you're friends with on Greentime"
          />
          <Row
            checked={isAppWide}
            onChange={onToggleAppWide}
            color={APP_WIDE_GROUP.color}
            emoji={APP_WIDE_GROUP.emoji}
            name={APP_WIDE_GROUP.name}
            hint="Discoverable to nearby Greentime users — no notifications"
          />

          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em',
                        color: '#9a9a8a', margin: '14px 0 7px' }}>
            Your groups
          </div>

          {groupsLoading ? (
            <p style={{ fontSize: 12, color: '#9a9a8a', margin: '0 0 8px 0' }}>Loading groups…</p>
          ) : groups.length === 0 ? (
            <p style={{ fontSize: 12, color: '#9a9a8a', margin: '0 0 8px 0' }}>
              No custom groups yet — create one in <strong>My Groups</strong>.
            </p>
          ) : (
            groups.map(g => (
              <Row
                key={g.id}
                checked={selectedGroupIds.includes(g.id)}
                onChange={() => onToggleGroup(g.id)}
                emoji={g.emoji}
                name={g.name}
                hint={g.is_public ? 'Public — anyone with the join code can join' : null}
              />
            ))
          )}
        </div>

        {/* Sticky footer — always reachable, unlike the old clipped one */}
        <div style={{ borderTop: '1px solid #f0ede4',
                      padding: '10px 16px calc(10px + env(safe-area-inset-bottom))' }}>
          <div style={{ fontSize: 11, marginBottom: 7,
                        color: selectedCount === 0 ? '#7a5a0a' : '#7a7a6a' }}>
            {selectedCount === 0
              ? 'Pick at least one — a tee time nobody can see has nobody to join it.'
              : `${selectedCount} selected`}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={onClose}
              style={{ flex: 1, fontSize: 13, padding: '9px 0', borderRadius: 8,
                       background: '#f4f1eb', color: '#0d2b1a',
                       border: '1px solid #ddd', cursor: 'pointer' }}>
              Cancel
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving || selectedCount === 0}
              style={{ flex: 1, fontSize: 13, padding: '9px 0', borderRadius: 8,
                       background: selectedCount === 0 ? '#9ab0a2' : '#0d2b1a',
                       color: '#7ec99a', border: 'none',
                       cursor: saving || selectedCount === 0 ? 'default' : 'pointer' }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Main TeeCard ─────────────────────────────────────────────
export default function TeeCard({ tt, currentUser, onRefresh, onViewThread, readonly }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  // Default the player section open for hosts who still have room — either an
  // actual open spot or headroom on the invited list — so the "+ Add player"
  // button is visible without a click. Also auto-open when the host has
  // pending player suggestions waiting on a response. Everyone else starts
  // collapsed.
  const [showPlayers, setShowPlayers] = useState(() => {
    if (readonly) return false;
    const viewerIsHost = tt.host_id === currentUser.id;
    if (!viewerIsHost) return false;
    const openSpots   = (tt.total_spots || 0) - (tt.player_count || 0);
    const invitedRoom = (tt.invited_count || 0) < (tt.total_spots || 0);
    const hasPendingSuggestions = Array.isArray(tt.suggestions) && tt.suggestions.length > 0;
    return openSpots > 0 || invitedRoom || hasPendingSuggestions;
  });
  const [showAddGuest, setShowAddGuest] = useState(false);
  const [showSuggest, setShowSuggest] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState((tt.groups || []).map(g => g.id));
  const [isEveryone, setIsEveryone] = useState(!!tt.is_everyone);
  const [isAppWide, setIsAppWide] = useState(!!tt.is_app_wide);
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [savingGroups, setSavingGroups] = useState(false);

  // Host-only waitlist data, lazy-loaded when player section is expanded
  const [waitlist, setWaitlist] = useState(null);
  const [waitlistLoading, setWaitlistLoading] = useState(false);

  // player_count from the server counts members + CONFIRMED guests only.
  // invited_count is the pending guests — shown to the host but not counted
  // against capacity.
  const left = tt.total_spots - tt.player_count;
  const invitedCount = tt.invited_count || 0;
  const isHost = tt.host_id === currentUser.id;
  const hasJoined = tt.players?.some(p => p.id === currentUser.id);
  const onWaitlist = tt.on_waitlist;
  const pct = Math.round((tt.player_count / tt.total_spots) * 100);
  const isFull = left === 0;
  const stripeClass = isFull ? 'full' : left === 1 ? 'warn' : '';

  // v99: Suggest Player assumes the suggester already knows a specific
  // person to recommend to the host for approval — a good fit for a
  // private/friends tee time, but not for one open to strangers (App-Wide,
  // Everyone, or a public join-code group), where the useful action is
  // "hand this to someone so they can see it and join themselves." Swap the
  // button accordingly; Suggest Player is untouched everywhere else.
  const isShareable = tt.is_everyone || tt.is_app_wide || (tt.groups || []).some(g => g.is_public);

  const loadGroups = async () => {
    if (groupsLoaded) return;
    setGroupsLoading(true);
    try { const g = await api.getGroups(); setGroups(g); setGroupsLoaded(true); }
    catch {}
    finally { setGroupsLoading(false); }
  };

  const toggleGroupId = (id) => {
    setSelectedGroupIds(prev =>
      prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]
    );
  };

  // v100: selections used to be seeded once at mount and never reset, so a
  // Cancel left the toggles dirty and the next open showed stale checkboxes
  // (and a refreshed tt never resynced them). Re-seed from props on open, and
  // open the sheet immediately rather than waiting on the groups fetch.
  const openGroupPicker = () => {
    setSelectedGroupIds((tt.groups || []).map(g => g.id));
    setIsEveryone(!!tt.is_everyone);
    setIsAppWide(!!tt.is_app_wide);
    setShowGroupPicker(true);
    loadGroups();
  };

  const handleSaveGroups = async () => {
    if (savingGroups) return;
    setSavingGroups(true);
    try {
      await api.updateTeeTime(tt.id, { groupIds: selectedGroupIds, isEveryone, isAppWide });
      setShowGroupPicker(false);
      onRefresh();
    } catch (err) { alert(err.message); }
    finally { setSavingGroups(false); }
  };

  const handleHostTogglePlaying = async () => {
    const opting = hasJoined; // currently playing → about to opt out
    if (opting) {
      const ok = window.confirm(
        `Sit out this tee time? You'll stay as host but free up a spot for someone else. You can rejoin if a spot is open.`
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      await api.hostTogglePlaying(tt.id);
      onRefresh();
    } catch (err) {
      if (err.message?.includes('full')) {
        alert("Can't rejoin — the tee time is full.");
      } else {
        alert(err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  // v99: shares a direct link to this tee time (/tee/:id). Visibility is
  // enforced server-side when the recipient opens it — this button just
  // hands out the link, nothing is granted here.
  const handleShareTeeTime = async () => {
    const shareUrl = `${window.location.origin}/tee/${tt.id}`;
    const shareText = `Join me for a round at ${tt.course_name} on ${fmtDate(tt.tee_date)}!`;
    if (navigator.share) {
      try { await navigator.share({ title: 'Greentime', text: shareText, url: shareUrl }); }
      catch { /* user cancelled the share sheet — not an error */ }
    } else {
      try {
        await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      } catch { /* clipboard blocked — nothing more we can do here */ }
    }
  };

  const handleJoin = async () => {
    setBusy(true);
    try {
      await api.joinTeeTime(tt.id);
      onRefresh();
    } catch (err) {
      if (err.message?.includes('full')) {
        // Offer waitlist
        if (window.confirm(`This tee time is full. Join the waitlist? You'll get a push notification if a spot opens.`)) {
          await handleJoinWaitlist();
        }
      } else {
        alert(err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleJoinWaitlist = async () => {
    setBusy(true);
    try {
      const { position } = await api.joinWaitlist(tt.id);
      onRefresh();
      alert(`You're #${position} on the waitlist. We'll notify you instantly if a spot opens.`);
    } catch (err) {
      alert(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleLeaveWaitlist = async () => {
    if (!window.confirm('Leave the waitlist?')) return;
    setBusy(true);
    try {
      await api.leaveWaitlist(tt.id);
      onRefresh();
    } catch (err) {
      alert(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = () => {
    setShowCancelModal(true);
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel your spot? The host will be notified.')) return;
    setBusy(true);
    try {
      await api.cancelSpot(tt.id);
      onRefresh();
    } catch (err) {
      alert(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveGuest = async (guestId) => {
    if (!window.confirm('Remove this guest from the tee time?')) return;
    try {
      await api.removeGuest(tt.id, guestId);
      onRefresh();
    } catch (err) {
      alert(err.message);
    }
  };

  // Host updates a guest's RSVP status. The server enforces the capacity guard
  // (confirming can't exceed total_spots) and the waitlist-notify side-effect,
  // so here we just call through and refresh — surfacing any 409 to the host.
  const handleSetGuestStatus = async (guest, status) => {
    try {
      await api.updateGuestStatus(tt.id, guest.id, status);
      onRefresh();
    } catch (err) {
      // Most likely a "tee time is full" 409 when confirming
      alert(err.message);
    }
  };

  // Host accepts or declines a pending player suggestion. The server resolves
  // an accept into a player or confirmed guest (capacity-guarded) and notifies
  // the suggester either way; we just call through and refresh, surfacing any
  // 409 (e.g. tee time full) to the host.
  const handleRespondToSuggestion = async (suggestionId, status) => {
    try {
      await api.respondToSuggestion(tt.id, suggestionId, status);
      onRefresh();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleRemovePlayer = async (player) => {
    if (!window.confirm(`Remove ${player.name} from this tee time? They'll get a notification.`)) return;
    try {
      await api.removePlayer(tt.id, player.id);
      onRefresh();
    } catch (err) {
      alert(err.message);
    }
  };

  // Lazy-load the host's waitlist view when the player section is expanded
  // and there are people on it. Refetches whenever the badge count changes.
  useEffect(() => {
    if (!isHost || !showPlayers || !tt.waitlist_count) {
      setWaitlist(null);
      return;
    }
    let cancelled = false;
    setWaitlistLoading(true);
    api.getWaitlist(tt.id)
      .then(rows => { if (!cancelled) setWaitlist(rows || []); })
      .catch(() => { if (!cancelled) setWaitlist([]); })
      .finally(() => { if (!cancelled) setWaitlistLoading(false); });
    return () => { cancelled = true; };
  }, [isHost, showPlayers, tt.id, tt.waitlist_count]);

  const handleSkipWaitlist = async (entry) => {
    if (!window.confirm(`Remove ${entry.user_name} from the waitlist? They'll get a notification.`)) return;
    try {
      await api.skipWaitlist(tt.id, entry.user_id);
      // Optimistic local update
      setWaitlist(prev => (prev || []).filter(w => w.user_id !== entry.user_id));
      onRefresh();
    } catch (err) {
      alert(err.message);
    }
  };

  // Reusable calendar download button — anyone who can see the tee time can download the .ics
  const CalendarButton = () => (
    <a
      href={api.getCalendarUrl(tt.id)}
      onClick={(e) => e.stopPropagation()}
      title="Add to calendar"
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 26, height: 22, padding: 0, fontSize: 13, lineHeight: 1,
        borderRadius: 6, border: '1px solid #d8d4c8', background: '#fff',
        textDecoration: 'none', color: '#0d2b1a', cursor: 'pointer',
      }}
      download
    >
      📅
    </a>
  );

  // v99: the Suggest Player slot becomes Share for tee times open to
  // strangers (App-Wide, Everyone, or a public join-code group) — the
  // useful action there is "let someone else see this," not "recommend a
  // specific known person to the host."
  const renderSuggestOrShare = () => (
    isShareable ? (
      <Tooltip text="Copy a link to this tee time so someone else can see it and join.">
        <button className="btn btn-outline btn-xs" onClick={(e) => { e.stopPropagation(); handleShareTeeTime(); }}>
          {shareCopied ? '✓ Copied!' : 'Share'}
        </button>
      </Tooltip>
    ) : (
      <Tooltip text="Recommend someone to the host. They'll get a notification and can accept or decline.">
        <button className="btn btn-outline btn-xs" onClick={(e) => { e.stopPropagation(); setShowSuggest(true); }}>Suggest player</button>
      </Tooltip>
    )
  );

  // Build the action button set
  const renderActions = () => {
    if (readonly) return (
      <div style={{ display: 'flex', gap: 4 }}>
        <Tooltip text="Download this tee time as a calendar event (.ics).">
          <CalendarButton />
        </Tooltip>
        <button className="btn btn-ghost btn-xs" onClick={(e) => { e.stopPropagation(); onViewThread(); }}>
          Thread{tt.comment_count > 0 ? ` (${tt.comment_count})` : ''}
        </button>
      </div>
    );
    if (isHost) return (
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        <Tooltip text="Download this tee time as a calendar event (.ics).">
          <CalendarButton />
        </Tooltip>
        <button className="btn btn-accent btn-xs" onClick={(e) => { e.stopPropagation(); setShowEdit(true); }}>Edit</button>
        {hasJoined ? (
          <Tooltip text="Free up your spot for someone else. You stay the host — just not playing. Rejoin anytime a spot's open.">
            <button className="btn btn-outline btn-xs" onClick={(e) => { e.stopPropagation(); handleHostTogglePlaying(); }} disabled={busy}>
              Sit out
            </button>
          </Tooltip>
        ) : (
          !isFull && (
            <Tooltip text="Take a playing spot again. You're the host either way.">
              <button className="btn btn-outline btn-xs" onClick={(e) => { e.stopPropagation(); handleHostTogglePlaying(); }} disabled={busy}>
                Rejoin
              </button>
            </Tooltip>
          )
        )}
        <Tooltip text="Cancels the whole tee time and notifies everyone who joined. This can't be undone.">
          <button className="btn btn-danger btn-xs" onClick={(e) => { e.stopPropagation(); handleDelete(); }} disabled={busy}>Cancel tee time</button>
        </Tooltip>
        <button className="btn btn-ghost btn-xs" onClick={(e) => { e.stopPropagation(); onViewThread(); }}>
          Thread{tt.comment_count > 0 ? ` (${tt.comment_count})` : ''}
        </button>
      </div>
    );
    if (hasJoined) return (
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        <Tooltip text="Download this tee time as a calendar event (.ics).">
          <CalendarButton />
        </Tooltip>
        <Tooltip text="Give up your spot. The host and waitlist are notified, and your spot opens up.">
          <button className="btn btn-danger btn-xs" onClick={(e) => { e.stopPropagation(); handleCancel(); }} disabled={busy}>Cancel</button>
        </Tooltip>
        {renderSuggestOrShare()}
        <button className="btn btn-ghost btn-xs" onClick={(e) => { e.stopPropagation(); onViewThread(); }}>Chat</button>
      </div>
    );
    if (onWaitlist) return (
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        <Tooltip text="Take yourself off the waitlist. You won't be notified if a spot opens.">
          <button className="btn btn-xs btn-outline" onClick={(e) => { e.stopPropagation(); handleLeaveWaitlist(); }} disabled={busy}
            style={{ borderColor: '#c8a443', color: '#7a5a0a' }}>
            On waitlist — leave?
          </button>
        </Tooltip>
        {renderSuggestOrShare()}
      </div>
    );
    if (isFull) return (
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        <Tooltip text="This tee time is full. Join the waitlist and you'll be notified if a spot opens.">
          <button className="btn btn-xs" onClick={(e) => { e.stopPropagation(); handleJoinWaitlist(); }} disabled={busy}
            style={{ background: '#faf0d8', color: '#7a5a0a', border: 'none' }}>
            Join waitlist
          </button>
        </Tooltip>
        {renderSuggestOrShare()}
      </div>
    );
    return (
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
        <Tooltip text="Grab a spot in this tee time.">
          <button className="btn btn-accent btn-sm" onClick={(e) => { e.stopPropagation(); handleJoin(); }} disabled={busy}>
            {busy ? '…' : 'Join'}
          </button>
        </Tooltip>
        {renderSuggestOrShare()}
      </div>
    );
  };

  return (
    <>
    <div className="tee-card">
      <div className={`tee-card-stripe ${stripeClass}`} />
      <div className="tee-card-body" onClick={onViewThread} style={{ cursor: 'pointer' }}>

        {/* Group badges + host assignment */}
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 5, marginBottom: (tt.groups?.length > 0) || tt.is_everyone || tt.is_app_wide || isHost ? 6 : 0 }}>
          {tt.is_everyone && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 3,
              fontSize: 11, padding: '2px 8px', borderRadius: 10,
              background: '#e8f5ec', color: '#1a5c32',
            }}>
              {EVERYONE_GROUP.emoji} {EVERYONE_GROUP.name}
            </div>
          )}
          {tt.is_app_wide && (
            <Tooltip text="Discoverable to any Greentime user nearby. No notifications — it just shows up in their Browse if they're within range.">
              <div
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                  fontSize: 11, padding: '2px 8px', borderRadius: 10,
                  background: '#faf6e8', color: '#7a5a0a',
                }}>
                {APP_WIDE_GROUP.emoji} {APP_WIDE_GROUP.name}
              </div>
            </Tooltip>
          )}
          {/* Distance pill — only shown when the server attached a distance_mi
              (i.e. this card was matched via App-Wide visibility from the user's
              anchor). Not shown for own/group tee times even if they happen to
              be near, to avoid clutter. */}
          {tt.is_app_wide && tt.distance_mi != null && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 3,
              fontSize: 11, padding: '2px 8px', borderRadius: 10,
              background: '#f4f1eb', color: '#5a5a4a',
            }}>
              📍 {Math.round(tt.distance_mi)} mi away
            </div>
          )}
          {(tt.groups || []).map(g => (
            <div key={g.id} style={{
              display: 'inline-flex', alignItems: 'center', gap: 3,
              fontSize: 11, padding: '2px 8px', borderRadius: 10,
              background: '#EEEDFE', color: '#3C3489',
            }}>
              {g.emoji} {g.name}
              {g.is_public && (
                <Tooltip text="Public group — anyone with the join code can join">
                  <span style={{ fontSize: 9 }}>🌐</span>
                </Tooltip>
              )}
            </div>
          ))}
          {isHost && !readonly && (
            <div onClick={(e) => e.stopPropagation()}>
              <button
                aria-expanded={showGroupPicker}
                style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, border: '1px solid #ddd',
                         background: '#f4f1eb', color: '#0d2b1a', cursor: 'pointer' }}
                onClick={() => showGroupPicker ? setShowGroupPicker(false) : openGroupPicker()}
              >
                {(tt.groups?.length > 0 || tt.is_everyone || tt.is_app_wide) ? '✎ Groups' : '+ Group'}
              </button>
              {showGroupPicker && (
                <GroupPickerSheet
                  courseName={tt.course_name}
                  groups={groups}
                  groupsLoading={groupsLoading}
                  saving={savingGroups}
                  isEveryone={isEveryone}
                  isAppWide={isAppWide}
                  selectedGroupIds={selectedGroupIds}
                  onToggleEveryone={() => setIsEveryone(v => !v)}
                  onToggleAppWide={() => setIsAppWide(v => !v)}
                  onToggleGroup={toggleGroupId}
                  onSave={handleSaveGroups}
                  onClose={() => setShowGroupPicker(false)}
                />
              )}
            </div>
          )}
        </div>

        {/* Course name */}
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 15, fontWeight: 500, color: '#0d2b1a' }}>
          {tt.course_url
            ? <a href={tt.course_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                 style={{ color: '#0d2b1a', textDecoration: 'none', borderBottom: '1px dashed #5db87a' }}>
                {tt.course_name}
              </a>
            : tt.course_name}
        </div>
        <div style={{ fontSize: 11, color: '#7a7a6a', marginBottom: '0.5rem' }}>
          Host: {tt.host_name}
          {tt.players && !tt.players.some(p => p.id === tt.host_id) && (
            <span style={{ marginLeft: 6, fontStyle: 'italic', color: '#9a9a8a' }}>(not playing)</span>
          )}
        </div>

        {/* Date + time */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5, marginBottom: '0.65rem' }}>
          <div>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: '#9a9a8a' }}>Date</div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{fmtDate(tt.tee_date)}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: '#9a9a8a' }}>Tee time</div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{fmtTime(tt.tee_time)}</div>
          </div>
        </div>

        {/* Weather */}
        <WeatherStrip weather={tt.weather} />

        {/* Spots progress */}
        <div className="progress-bar" style={{ marginBottom: 3 }}>
          <div className={`progress-fill${pct >= 100 ? ' full' : pct >= 66 ? ' warn' : ''}`}
               style={{ width: `${pct}%` }} />
        </div>
        <div style={{ fontSize: 11, color: '#7a7a6a', marginBottom: '0.5rem' }}>
          <strong>{tt.player_count}/{tt.total_spots}</strong> golfers
          {invitedCount > 0 && (
            <span style={{ color: '#9a8a5a' }}> · {invitedCount} invited</span>
          )}
          {' · '}<strong>{tt.holes || 18}</strong> holes
          {tt.comment_count > 0 && ` · ${tt.comment_count} msg`}
        </div>

        {/* Player chips with handicaps — toggle on click */}
        {tt.players && tt.players.length > 0 && (
          <div style={{ marginBottom: '0.6rem' }}>
            <button onClick={(e) => { e.stopPropagation(); setShowPlayers(v => !v); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer',
                       fontSize: 11, color: '#7a7a6a', padding: 0, marginBottom: 4,
                       display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span>{showPlayers ? '▲ Hide players' : '▼ Show players'}</span>
              {/* Nudge the host when suggestions are waiting — visible even when
                  the section is collapsed. */}
              {isHost && Array.isArray(tt.suggestions) && tt.suggestions.length > 0 && (
                <span style={{
                  fontSize: 10, fontWeight: 500, padding: '1px 7px', borderRadius: 20,
                  background: '#dfe9f7', color: '#3a5a8a',
                }}>
                  {tt.suggestions.length} suggested
                </span>
              )}
            </button>
            {showPlayers && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                {(() => {
                  const displayNames = computeDisplayNames(tt.players);
                  return tt.players.map(p => (
                    <PlayerChip
                      key={p.id}
                      player={p}
                      displayName={displayNames[p.id]}
                      isHost={p.id === tt.host_id}
                      isCurrentUser={p.id === currentUser.id}
                      canRemove={isHost && p.id !== tt.host_id}
                      onRemove={() => p.type === 'guest' ? handleRemoveGuest(p.id) : handleRemovePlayer(p)}
                      onProfileClick={(player) => navigate(`/friends?user=${player.id}`)}
                      viewerIsHost={isHost && !readonly}
                      onSetGuestStatus={handleSetGuestStatus}
                    />
                  ));
                })()}
                {/* Add player button for host — only shown when expanded.
                    Shown when there's a real open spot OR room on the invited
                    list (the host can build a backup list of invites even when
                    confirmed spots are full). The backend enforces the actual
                    limits. */}
                {isHost && !readonly && (!isFull || invitedCount < tt.total_spots) && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowAddGuest(true); }}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      background: 'none', border: '1.5px dashed #7ec99a',
                      borderRadius: 20, padding: '2px 10px', fontSize: 11,
                      color: '#2e7d4f', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    + Add player
                  </button>
                )}
              </div>
            )}

            {/* Host-only waitlist section — visible only when host has expanded
                the players section and the tee time has at least one waitlister */}
            {showPlayers && isHost && tt.waitlist_count > 0 && (
              <div style={{
                marginTop: 8, padding: '8px 10px',
                background: '#faf6e8', border: '1px solid #ebe0bd', borderRadius: 8,
              }}>
                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em',
                              color: '#7a5a0a', marginBottom: 6, fontWeight: 500 }}>
                  Waitlist ({tt.waitlist_count})
                </div>
                {waitlistLoading && !waitlist && (
                  <div style={{ fontSize: 11, color: '#9a8a5a' }}>Loading…</div>
                )}
                {waitlist && waitlist.length === 0 && (
                  <div style={{ fontSize: 11, color: '#9a8a5a' }}>No one on the waitlist.</div>
                )}
                {waitlist && waitlist.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {waitlist.map((w, idx) => {
                      const activeClaim = w.notified_at && w.expires_at && new Date(w.expires_at) > new Date();
                      return (
                        <div key={w.user_id} style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          fontSize: 12, color: '#5a4a1a',
                        }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 18, height: 18, borderRadius: '50%',
                            background: '#ebe0bd', color: '#7a5a0a',
                            fontSize: 10, fontWeight: 600, flexShrink: 0,
                          }}>{idx + 1}</span>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 22, height: 22, borderRadius: '50%',
                            background: '#fff5d8', color: '#5a4a1a',
                            fontSize: 10, fontWeight: 600, flexShrink: 0,
                          }}>{w.initials}</span>
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ fontWeight: 500 }}>{w.user_name}</span>
                            <span style={{ marginLeft: 6, fontSize: 10, color: '#9a8a5a' }}>
                              joined {fmtJoinedAt(w.joined_at)}
                            </span>
                            {activeClaim && (
                              <span style={{
                                marginLeft: 6, fontSize: 9, padding: '1px 6px',
                                background: '#fff', border: '1px solid #ebe0bd',
                                borderRadius: 10, color: '#7a5a0a', fontWeight: 500,
                              }}>
                                ✉ notified
                              </span>
                            )}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleSkipWaitlist(w); }}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              fontSize: 11, color: '#9a8a5a', padding: '2px 6px',
                              textDecoration: 'underline', textUnderlineOffset: 2,
                            }}
                            title={`Remove ${w.user_name} from waitlist`}
                          >
                            Skip
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Host-only: pending player suggestions from friends. Each shows
                the suggester, who they suggested, the optional message, and
                Accept / Decline. tt.suggestions is only populated for the host
                (server returns null for everyone else). */}
            {showPlayers && isHost && Array.isArray(tt.suggestions) && tt.suggestions.length > 0 && (
              <div style={{
                marginTop: 8, padding: '8px 10px',
                background: '#eef3fb', border: '1px solid #c9d6ea', borderRadius: 8,
              }}>
                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em',
                              color: '#3a5a8a', marginBottom: 6, fontWeight: 500 }}>
                  Suggested players ({tt.suggestions.length})
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {tt.suggestions.map(s => (
                    <div key={s.id} style={{
                      display: 'flex', flexDirection: 'column', gap: 4,
                      paddingBottom: 8, borderBottom: '0.5px solid #d8e0ee',
                    }}>
                      <div style={{ fontSize: 12, color: '#2a3a52' }}>
                        <strong>{s.suggested_name}</strong>
                        {s.suggested_user_id
                          ? <span style={{ fontSize: 10, color: '#5a7aaa', marginLeft: 5 }}>Greentime user</span>
                          : <span style={{ fontSize: 10, color: '#5a7aaa', marginLeft: 5 }}>guest</span>}
                      </div>
                      <div style={{ fontSize: 11, color: '#5a6a82' }}>
                        Suggested by {s.suggester_name}
                      </div>
                      {s.message && (
                        <div style={{
                          fontSize: 11, color: '#3a4a62', fontStyle: 'italic',
                          background: '#fff', borderRadius: 6, padding: '5px 8px',
                          border: '0.5px solid #d8e0ee',
                        }}>
                          “{s.message}”
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 5, marginTop: 2 }}>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleRespondToSuggestion(s.id, 'accepted'); }}
                          className="btn btn-accent btn-xs"
                        >
                          Accept
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleRespondToSuggestion(s.id, 'declined'); }}
                          className="btn btn-outline btn-xs"
                        >
                          Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* If no players shown yet, still give host the add button */}
        {(!tt.players || tt.players.length === 0) && isHost && !readonly
          && (!isFull || invitedCount < tt.total_spots) && (
          <div style={{ marginBottom: '0.6rem' }}>
            <button
              onClick={(e) => { e.stopPropagation(); setShowAddGuest(true); }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: 'none', border: '1.5px dashed #7ec99a',
                borderRadius: 20, padding: '2px 10px', fontSize: 11,
                color: '#2e7d4f', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
              }}
            >
              + Add player
            </button>
          </div>
        )}

        {/* Footer: badges + action */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      flexWrap: 'wrap', gap: 5 }}>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
            {isFull
              ? <span className="badge badge-full">Full</span>
              : left === 1
                ? <span className="badge badge-few">1 left</span>
                : <span className="badge badge-open">{left} open</span>}
            <WaitlistBadge count={tt.waitlist_count} />
          </div>
          {renderActions()}
        </div>

      </div>
    </div>

    {showAddGuest && (
      <AddGuestModal
        teeTime={tt}
        onClose={() => setShowAddGuest(false)}
        onAdded={() => { setShowAddGuest(false); onRefresh(); }}
      />
    )}
    {showSuggest && (
      <SuggestPlayerModal
        teeTime={tt}
        onClose={() => setShowSuggest(false)}
        onSuggested={() => { setShowSuggest(false); onRefresh(); }}
      />
    )}
    {showEdit && (
      <EditTeeTimeModal
        tt={tt}
        onClose={() => setShowEdit(false)}
        onSaved={() => { setShowEdit(false); onRefresh(); }}
      />
    )}
    {showCancelModal && (
      <CancelTeeTimeModal
        tt={tt}
        currentUser={currentUser}
        onClose={() => setShowCancelModal(false)}
        onConfirmed={() => { setShowCancelModal(false); onRefresh(); }}
      />
    )}
    </>
  );
}

